import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import { usePermission } from 'hooks/usePermission'
import ExtraFieldModal from 'templates/Settings/Body/Sections/ExtraFields/components/ExtraFieldModal/ExtraFields.Modal'
import {
  ExtraFieldState,
  ExtraFieldsProvider,
  useExtraFieldsContext
} from 'templates/Settings/Body/Sections/ExtraFields/Extrafields.context'
import { invalidateCustomFieldsCache } from 'templates/v2/RegistrationLink/components/Info/ExtraInfo/components/ExtraFieldsConfiguration/components/ExtraFieldsAutocomplete'

import { ExtraFieldItem } from './index'

// Reflete na lista do link o campo que acabou de ser salvo no modal
export const applyExtraFieldUpdate = (
  items: ExtraFieldItem[],
  field: ExtraFieldState
): ExtraFieldItem[] =>
  items.map((item) =>
    item.uuid === field.uuid
      ? {
          ...item,
          name: field.field_configuration?.title || item.name,
          field_type: field.field_type,
          extra_fieldable_type: field.field_type,
          options: field.field_configuration?.options,
          field_configuration: field.field_configuration,
          upload_settings: field.upload_settings
        }
      : item
  )

export interface ExtraFieldEditorProps {
  uuid: string | null
  onClose: () => void
  onUpdated: (field: ExtraFieldState) => void
}

const ExtraFieldEditorModal = ({
  uuid,
  onClose,
  onUpdated
}: ExtraFieldEditorProps) => {
  const { edit } = useExtraFieldsContext()
  const wasOpen = useRef(false)

  useEffect(() => {
    if (uuid) edit.handleOpen(uuid)
  }, [uuid])

  useEffect(() => {
    if (edit.open) {
      wasOpen.current = true
    } else if (wasOpen.current) {
      wasOpen.current = false
      onClose()
    }
  }, [edit.open])

  // Se o campo não carregou, fecha em vez de cair no modo "Novo campo"
  useEffect(() => {
    if (uuid && edit.open && !edit.loading && !edit.data) edit.handleClose()
  }, [edit.open, edit.loading, edit.data])

  return <ExtraFieldModal onFieldUpdated={onUpdated} />
}

// Abre o modal de edição de campo extra (o mesmo das Configurações) sem sair
// da tela em que o campo está sendo configurado.
const ExtraFieldEditor = (props: ExtraFieldEditorProps) => (
  <ExtraFieldsProvider shouldRun={false}>
    <ExtraFieldEditorModal {...props} />
  </ExtraFieldsProvider>
)

// Estado da edição + props prontas pra linha (onEdit) e pro ExtraFieldEditor.
// onEdit fica undefined sem custom_fields:manage, e a linha não vira botão.
export const useExtraFieldEditor = (
  setExtraFields: Dispatch<SetStateAction<ExtraFieldItem[]>>
) => {
  const canManageExtraFields = usePermission('custom_fields:manage')
  const [editingUuid, setEditingUuid] = useState<string | null>(null)

  const onClose = useCallback(() => setEditingUuid(null), [])

  const onUpdated = useCallback(
    (field: ExtraFieldState) => {
      invalidateCustomFieldsCache()
      setExtraFields((prev) => applyExtraFieldUpdate(prev, field))
    },
    [setExtraFields]
  )

  return {
    onEdit: canManageExtraFields ? setEditingUuid : undefined,
    editorProps: { uuid: editingUuid, onClose, onUpdated }
  }
}

export default ExtraFieldEditor
